import { POI_DISPLAY_TYPE } from '@/enums/poi';
import type { BuildingCategory, BuildingInStoreInfo, FloorInfo } from '@/services/dto';

const optionAllCategory: BuildingCategory = {
    clientCode: 'all',
    name: {
        ko: 'common.text.all_category',
        en: 'common.text.all_category',
        ja: 'common.text.all_category',
        'zh-CN': 'common.text.all_category',
        'zh-TW': 'common.text.all_category',
    },
    id: '',
    parentClientCode: '',
    indentLevel: 0,
    categoryCode: '',
    parentCategoryCode: '',
};
import shopUtils, { getSearchMenu, resolveShopPoiContents } from '@/utils/shop';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useConfigStore } from './configStore';
import type { TFunction } from 'i18next';
import { SHOP_SEARCH_MODE } from '@/enums/shop';
import type { ShopState, ShopActions, ShopFilterConfig } from '@/stores/types/shopStore';
import type { PoiContentSlide } from '@/types/shop';


interface ShopStore extends ShopState, ShopActions {}

export const useShopStore = create<ShopStore>()(
    devtools(
        (set, get) => ({
            // Initial state
            shopContents: [],
            rawShopContents: [],
            searchText: '',
            selectedFloor: {
                id: 'all',
                label: '',
                value: '',
                data: null,
            },
            selectedBuildingId: 'all',
            selectedCategory: null,
            t: null,
            currentMode: SHOP_SEARCH_MODE.SEARCH_BY_TEXT,
            keyboardLanguage: 'ko',

            setKeyboardLanguage: (language: 'ko' | 'en') => {
                const prev = get().keyboardLanguage;
                if (prev === language) return;

                set({ keyboardLanguage: language });
            },

            setCurrentMode: (mode: SHOP_SEARCH_MODE) => {
                const prev = get().currentMode;
                if (prev === mode) return;

                set({ currentMode: mode });
            },

            setSearchText: (text: string) => {
                const prev = get().searchText;
                if (prev === text) return;

                set({ searchText: text });
            },

            setSelectedFloor: (floor: { id: string; label: string; value: string; data: FloorInfo | null | string[] } | null) => {
                const prev = get().selectedFloor;
                if (prev?.id === floor?.id) return;

                set({ selectedFloor: floor });
            },

            setSelectedBuildingId: (buildingId: string | null) => {
                const prev = get().selectedBuildingId;
                if (prev === buildingId) return;

                set({ selectedBuildingId: buildingId });
            },

            setSelectedCategory: (category: BuildingCategory) => {
                const prev = get().selectedCategory;
                if (prev?.clientCode === category?.clientCode) return;

                set({ selectedCategory: category });
            },

            setTranslationFunction: (t: TFunction<'translation', undefined>) => {
                set({ t });
            },

            resetFilters: () => {
                const state = get();
                const { t } = state;

                const { config } = useConfigStore.getState();
                const { server } = config;

                const allFloorText = t ? t('shop.search.text.content.all_floor') : 'All Floors';
                const isMultipleBuildings = server?.store?.buildings && server?.store?.buildings?.length > 1;
                const newBuildingId = isMultipleBuildings ? 'all' : (server?.store?.buildings?.[0]?.id ?? 'all');

                set({
                    searchText: '',
                    selectedFloor: {
                        id: 'all',
                        label: allFloorText,
                        value: 'all',
                        data: null,
                    },
                    selectedBuildingId: newBuildingId,
                    selectedCategory: optionAllCategory,
                });
            },

            updateShopContents: (searchMode: SHOP_SEARCH_MODE): PoiContentSlide[] => {
                const state = get();
                const { selectedFloor, selectedBuildingId, selectedCategory, searchText, keyboardLanguage } = state;
                const { config } = useConfigStore.getState();
                const {
                    ui: { language },
                } = config;

                const allBuildings = config.server?.store?.buildings ?? [];
                if (!allBuildings.length) {
                    if (state.shopContents?.length > 0) {
                        set({ shopContents: [] });
                    }
                    return [];
                }

                const searchMenu = getSearchMenu(config.server?.buildingMenus, language);
                const pois = resolveShopPoiContents(searchMenu, selectedBuildingId, searchMode);

                const menuCategories = searchMenu?.selectedCategories;
                const rawShopContents = shopUtils.convertPoiContentToSlide(
                    language,
                    allBuildings,
                    pois.length ? pois : undefined,
                    searchText,
                    menuCategories
                );

                const searchMenuForFilter = getSearchMenu(config.server?.buildingMenus, language);
                const menuCategoryForFilter = searchMenuForFilter?.selectedCategories?.find(
                    (c) => c.clientCode === selectedCategory?.clientCode
                );
                const categoryCodesForFilter: string[] = menuCategoryForFilter?.mergeCategoryCode ?? [];

                // const contentsPropertyManagement = config.server?.store?.contentsPropertyManagement ?? [];
                // const keywordSearchEnabled = contentsPropertyManagement.some(
                //     (item) => item.display_type === POI_DISPLAY_TYPE.KEY_WORDS && item.visible !== false
                // );

                const keywordSearchEnabled = false;

                const filterConfig: ShopFilterConfig = {
                    language,
                    selectedBuildingId,
                    selectedFloor,
                    selectedCategory,
                    searchText,
                    buildings: config.server?.store?.buildings,
                    categoryCodesForFilter,
                    keywordSearchEnabled,
                };

                if (!rawShopContents?.length) {
                    if (state.shopContents?.length > 0) {
                        set({ shopContents: [] });
                    }
                    return [];
                }

                const shopFilter = shopUtils.createShopFilter(searchMode, filterConfig, keyboardLanguage);
                const filteredContents = rawShopContents.filter(shopFilter);

                if (filteredContents?.length === 0) {
                    if (state.shopContents?.length > 0) {
                        set({ shopContents: [] });
                    }
                    return [];
                }

                const getPriority = (char: string) => {
                    if (!char) return 4;
                    if (/[ㄱ-ㅎㅏ-ㅣ가-힣]/.test(char)) return 1;
                    if (/[a-zA-Z]/.test(char)) return 2;
                    if (/[0-9]/.test(char)) return 3;
                    return 4;
                };

                const sortedContents = filteredContents.sort((a, b) => {
                    const nameA = (a?.data?.name?.[language] || '').trim();
                    const nameB = (b?.data?.name?.[language] || '').trim();

                    const priorityA = getPriority(nameA.charAt(0));
                    const priorityB = getPriority(nameB.charAt(0));

                    if (priorityA !== priorityB) {
                        return priorityA - priorityB;
                    }

                    return nameA.localeCompare(nameB, language, {
                        numeric: true,
                        sensitivity: 'base',
                        ignorePunctuation: true,
                    });
                });

                set({ shopContents: sortedContents });

                return sortedContents;
            },

            initialize: (t: TFunction<'translation', undefined>) => {
                const state = get();
                if (state.t === t && state.rawShopContents?.length > 0) {
                    return;
                }

                const { config } = useConfigStore.getState();
                const { ui: { language }, server } = config;

                const allBuildings = server?.store?.buildings ?? [];
                const selectedBuildingId = allBuildings.length === 1 ? allBuildings[0].id : 'all';
                const searchMenu = getSearchMenu(server?.buildingMenus, language);
                const pois = resolveShopPoiContents(searchMenu, selectedBuildingId, SHOP_SEARCH_MODE.SEARCH_BY_TEXT);
                const menuCategories = searchMenu?.selectedCategories;
                const rawShopContents = shopUtils.convertPoiContentToSlide(
                    language,
                    allBuildings,
                    pois.length ? pois : undefined,
                    undefined,
                    menuCategories
                );
                const updates: Partial<ShopState> = {
                    t,
                    rawShopContents,
                    currentMode: SHOP_SEARCH_MODE.SEARCH_BY_TEXT,
                    selectedBuildingId,
                    selectedCategory: optionAllCategory,
                };

                set(updates);
            },
        }),
        {
            name: 'shop-store',
        }
    )
);

let previousBuildings: BuildingInStoreInfo[] | undefined = undefined;
let previousLanguage: string = '';
let previousBuildingMenus: Record<string, unknown> | undefined = undefined;

useConfigStore.subscribe(() => {
    const { config } = useConfigStore.getState();
    const currentBuildings = config.server?.store?.buildings;
    const currentLanguage = config.ui?.language;
    const currentBuildingMenus = config.server?.buildingMenus as Record<string, unknown> | undefined;

    if (currentBuildings !== previousBuildings || currentLanguage !== previousLanguage || currentBuildingMenus !== previousBuildingMenus) {
        previousBuildings = currentBuildings;
        previousLanguage = currentLanguage;
        previousBuildingMenus = currentBuildingMenus;

        const { initialize, t } = useShopStore.getState();
        if (t) {
            initialize(t);
        }
    }
});
